import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RefreshCw } from 'lucide-react';

export default function ReAnalyzeModal({ isOpen, onClose, onReAnalyze, currentFocus = 'both', currentExpertiseLevel = 'beginner' }) {
  const [analysisFocus, setAnalysisFocus] = useState(currentFocus);
  const [expertiseLevel, setExpertiseLevel] = useState(currentExpertiseLevel);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleSubmit = async () => {
    setIsAnalyzing(true);
    try {
      await onReAnalyze({ analysisFocus, expertiseLevel });
      onClose();
    } catch (error) {
      console.error('Re-analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getFocusLabel = (value) => {
    if (value === 'content') return 'Content Only';
    if (value === 'design') return 'Design Only';
    return 'Content + Design';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-prezlab-mint" />
            Re-Analyze Presentation
          </DialogTitle>
          <DialogDescription>
            Adjust your analysis preferences and get fresh insights
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Analysis Focus Buttons */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Analysis Type</Label>
              <p className="text-sm text-muted-foreground">
                Choose what aspects to analyze
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setAnalysisFocus('content')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  analysisFocus === 'content'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                    : 'border-border bg-background/60 hover:border-blue-300 hover:bg-blue-50/50'
                }`}
              >
                <div className="text-center space-y-2">
                  <div className={`text-2xl ${analysisFocus === 'content' ? 'text-blue-600' : 'text-foreground'}`}>
                    📝
                  </div>
                  <div className={`text-sm font-semibold ${analysisFocus === 'content' ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'}`}>
                    Content Only
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setAnalysisFocus('design')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  analysisFocus === 'design'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                    : 'border-border bg-background/60 hover:border-purple-300 hover:bg-purple-50/50'
                }`}
              >
                <div className="text-center space-y-2">
                  <div className={`text-2xl ${analysisFocus === 'design' ? 'text-purple-600' : 'text-foreground'}`}>
                    🎨
                  </div>
                  <div className={`text-sm font-semibold ${analysisFocus === 'design' ? 'text-purple-700 dark:text-purple-400' : 'text-foreground'}`}>
                    Design Only
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setAnalysisFocus('both')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  analysisFocus === 'both'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md'
                    : 'border-border bg-background/60 hover:border-green-300 hover:bg-green-50/50'
                }`}
              >
                <div className="text-center space-y-2">
                  <div className={`text-2xl ${analysisFocus === 'both' ? 'text-green-600' : 'text-foreground'}`}>
                    ⚡
                  </div>
                  <div className={`text-sm font-semibold ${analysisFocus === 'both' ? 'text-green-700 dark:text-green-400' : 'text-foreground'}`}>
                    Both
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Expertise Level Selection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>🎯 How do you prefer your feedback?</Label>
              <p className="text-sm text-muted-foreground">
                Choose the language style for recommendations
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setExpertiseLevel('beginner')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  expertiseLevel === 'beginner'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                    : 'border-border bg-background/60 hover:border-blue-300 hover:bg-blue-50/50'
                }`}
              >
                <div className="text-center space-y-2">
                  <div className={`text-2xl ${expertiseLevel === 'beginner' ? 'text-blue-600' : 'text-foreground'}`}>
                    💬
                  </div>
                  <div className={`text-sm font-semibold ${expertiseLevel === 'beginner' ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'}`}>
                    Keep it simple
                  </div>
                  <div className="text-xs text-muted-foreground">Plain language</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setExpertiseLevel('expert')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  expertiseLevel === 'expert'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                    : 'border-border bg-background/60 hover:border-purple-300 hover:bg-purple-50/50'
                }`}
              >
                <div className="text-center space-y-2">
                  <div className={`text-2xl ${expertiseLevel === 'expert' ? 'text-purple-600' : 'text-foreground'}`}>
                    🔬
                  </div>
                  <div className={`text-sm font-semibold ${expertiseLevel === 'expert' ? 'text-purple-700 dark:text-purple-400' : 'text-foreground'}`}>
                    I speak design
                  </div>
                  <div className="text-xs text-muted-foreground">Technical terms</div>
                </div>
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <p className="text-muted-foreground">
              <strong className="text-foreground">What's changing:</strong>
              <br />
              • AI will re-analyze your presentation with the new focus
              <br />
              • Slide recommendations will be regenerated
              <br />
              • Overall score may change
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isAnalyzing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Re-Analyze
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
